import { ReactElement, useCallback, useState, useEffect } from 'react';
import { Dropdown, SubmitButton, TextInput, RadioButtonChip } from '@components/common';
import { useFormik } from 'formik';
import {
	commercialTypeOfPropertyList,
	siteInfoInitialValues,
	propertyAreaList,
	residentialTypeOfPropertyList,
	siteStageList,
	siteTypeList,
	SiteTypeLabel,
	SiteStageLabel,
	TilingWorkList,
	TilingWorkStatusList,
} from '.';
import { useNavigate } from 'react-router-dom';
import { useValidation } from '@hooks/validations';
import { useEndUserStore } from '@store/enduser';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useSiteContext } from '@components/lms/addNewSite';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { LocationModal } from '@components/common/locationModal/locationModal';
import SquareCheckbox, { SquareCheckboxGroup } from '@components/common/formControl/square-checkbox';
import RoundedCheckbox, { RoundedCheckboxGroup } from '@components/common/formControl/rounded-checkbox';

export const SiteInformation = (): ReactElement => {
	const navigate = useNavigate();

	const store = useAddNewSiteStore();
	const { activeTabIndex, setActiveTabIndex, isConvertingUpcomingToActive } = useEndUserStore();
	const { setSubKeyValue } = useAddNewSiteStore();
	const { LmsSiteInfoValidationSchema } = useValidation();
	const { siteData, isEditing } = useSiteContext();
	const { clearSiteDetails } = useSiteAllDetailsStore();

	useSetMainHeader(isEditing ? 'Site Details' : 'Add New Site');

	// Clear site details store when component unmounts for new sites
	useEffect(() => {
		return (): void => {
			if (!isEditing) {
				clearSiteDetails();
			}
		};
	}, [isEditing, clearSiteDetails]);

	const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
	const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);
	const [hasCheckedLocationPermission, setHasCheckedLocationPermission] = useState(false);
	const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

	const formik = useFormik({
		initialValues: {
			siteName: store.siteInfo.siteName || siteInfoInitialValues.siteName,
			siteLocation: store.siteInfo.siteLocation || siteInfoInitialValues.siteLocation,
			siteAddress: store.siteInfo.siteAddress || siteInfoInitialValues.siteAddress,
			longitude: store.siteInfo.longitude || siteInfoInitialValues.longitude,
			latitude: store.siteInfo.latitude || siteInfoInitialValues.latitude,
			pincode: store.siteInfo.pincode || siteInfoInitialValues.pincode,
			city: store.siteInfo.city || siteInfoInitialValues.city,
			siteType: store.siteInfo.siteType || siteInfoInitialValues.siteType,
			typeOfProperty: store.siteInfo.typeOfProperty || siteInfoInitialValues.typeOfProperty,
			propertyArea: store.siteInfo.propertyArea || siteInfoInitialValues.propertyArea,
			siteStage: isConvertingUpcomingToActive
				? SiteStageLabel.Active
				: store.siteInfo.siteStage || siteInfoInitialValues.siteStage,
			is_bathroom: store.siteInfo?.is_bathroom ?? false,
			is_other_rooms: store.siteInfo?.is_other_rooms ?? false,
			bathroom_work_status: store.siteInfo?.bathroom_work_status ?? '',
			other_rooms_work_status: store.siteInfo?.other_rooms_work_status ?? '',
			typeOfContractor: '', // View-only field, NOT saved to store or payload
			contractorPotentialPerMonth: '', // View-only field, NOT saved to store or payload
			noOfSites: '', // View-only field, NOT saved to store or payload
		},
		enableReinitialize: true, // important!
		validationSchema: LmsSiteInfoValidationSchema,
		onSubmit: () => {
			handleNext();
		},
	});

	const siteType = formik.values.siteType;

	// Location permission handlers
	const handleLocationPermissionSuccess = useCallback(
		(latitude: number, longitude: number) => {
			setLocationPermissionGranted(true);
			setShowLocationPermissionModal(false);

			// Auto-fill location data immediately
			formik.setFieldValue('latitude', latitude.toString());
			formik.setFieldValue('longitude', longitude.toString());

			// Use reverse geocoding to get address details
			const geocoder = new google.maps.Geocoder();
			geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
				if (status === 'OK' && results && results[0]) {
					const result = results[0];
					const addressComponents = result.address_components;

					// Extract city and pincode
					let city = '';
					let pincode = '';

					addressComponents.forEach((component) => {
						const types = component.types;

						if (types.includes('locality')) {
							city = component.long_name;
						}
						if (!city && types.includes('administrative_area_level_3')) {
							city = component.long_name;
						}
						if (!city && types.includes('administrative_area_level_2')) {
							city = component.long_name;
						}
						if (component.types.includes('postal_code')) {
							pincode = component.long_name;
						}
					});

					// Auto-fill all location fields
					formik.setFieldValue('siteLocation', result.formatted_address);
					formik.setFieldValue('city', city);
					formik.setFieldValue('pincode', pincode);
				}
			});
		},
		[formik]
	);

	// Location handlers
	const handleLocationSuccess = useCallback(
		(lat: number, lng: number): void => {
			// Close modal immediately when location is obtained
			setShowLocationPermissionModal(false);
			setLocationPermissionDenied(false);

			// Explicitly remove overlay and close dialog immediately to ensure they're removed
			// even if Modal component's useEffect hasn't run yet
			const overlay = document.getElementById('overlay') as HTMLElement;
			if (overlay) {
				overlay.classList.remove('show');
			}
			document.body.classList.remove('overflow-hidden');

			// Close the dialog element directly
			const dialog = document.getElementById('locationPermissionModal') as HTMLDialogElement;
			if (dialog) {
				dialog.close();
			}

			// Then process location data
			handleLocationPermissionSuccess(lat, lng);
		},
		[handleLocationPermissionSuccess]
	);

	const handleLocationError = useCallback((_error: string): void => {
		// Don't show toaster here, let the modal handle the error display
		setLocationPermissionDenied(true);
		// Keep the modal open so user can try again
	}, []);

	// Handle modal close - called by LocationModal after successful location fetch
	// This is a fallback in case the modal didn't close via handleLocationSuccess
	const handleModalClose = useCallback((): void => {
		// Only close if location is already obtained (safety check)
		if (formik.values.latitude && formik.values.longitude) {
			setShowLocationPermissionModal(false);
			setLocationPermissionDenied(false);
		}
	}, [formik.values.latitude, formik.values.longitude]);

	// Request location permission manually
	const requestLocationPermission = useCallback(() => {
		setShowLocationPermissionModal(true);
		setLocationPermissionDenied(false);
	}, []);

	// Check location permission on component mount
	useEffect(() => {
		if (hasCheckedLocationPermission || isEditing) return;

		const checkLocationPermission = (): void => {
			if (!navigator.geolocation) {
				setShowLocationPermissionModal(true);
				setIsInitializing(false);
				setHasCheckedLocationPermission(true);
				setLocationPermissionDenied(true);
				return;
			}

			// Try to get current position without showing modal
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude: lat, longitude: lng } = position.coords;
					setShowLocationPermissionModal(false);
					setLocationPermissionDenied(false);
					setIsInitializing(false);
					setHasCheckedLocationPermission(true);
					handleLocationPermissionSuccess(lat, lng);
				},
				() => {
					// Permission not granted, show modal
					setShowLocationPermissionModal(true);
					setIsInitializing(false);
					setHasCheckedLocationPermission(true);
					setLocationPermissionDenied(false);
				},
				{
					enableHighAccuracy: true,
					timeout: 10000, // Increased timeout for better accuracy
					maximumAge: 300000, // 5 minutes
				}
			);
		};

		checkLocationPermission();
	}, [hasCheckedLocationPermission, isEditing, handleLocationPermissionSuccess]);

	const handleNext = useCallback(() => {
		// Check if location permission is granted and location data is available
		if (!isEditing && (!locationPermissionGranted || !formik.values.latitude || !formik.values.longitude)) {
			// Show location permission modal
			setShowLocationPermissionModal(true);
			return;
		}

		// Tiling work validation: only required when site stage is Upcoming
		if (formik.values.siteStage === SiteStageLabel.Upcoming) {
			const isBathroomSelected = Boolean(formik.values.is_bathroom);
			const isOtherRoomsSelected = Boolean(formik.values.is_other_rooms);
			if (!isBathroomSelected && !isOtherRoomsSelected) {
				formik.setFieldError('is_bathroom', 'Select at least one area (Bathroom or Other Rooms)');
				return;
			}
			if (isBathroomSelected && !formik.values.bathroom_work_status) {
				formik.setFieldError('bathroom_work_status', 'Select status for Bathroom');
				return;
			}
			if (isOtherRoomsSelected && !formik.values.other_rooms_work_status) {
				formik.setFieldError('other_rooms_work_status', 'Select status for Other Rooms');
				return;
			}
		}

		const keys = [
			'siteName',
			'siteLocation',
			'siteAddress',
			'longitude',
			'latitude',
			'pincode',
			'city',
			'siteType',
			'typeOfProperty',
			'propertyArea',
			'siteStage',
			'is_bathroom',
			'is_other_rooms',
			'bathroom_work_status',
			'other_rooms_work_status',
		] as const;

		keys.forEach((key) => {
			setSubKeyValue('siteInfo', key, formik.values[key]);
		});

		setActiveTabIndex(activeTabIndex + 1);
		navigate(
			formik.values.siteStage === SiteStageLabel.Upcoming
				? '/lms/add-new-site/other-information'
				: '/lms/add-new-site/meeting-details'
		);
	}, [
		activeTabIndex,
		formik.values,
		navigate,
		setActiveTabIndex,
		setSubKeyValue,
		isEditing,
		locationPermissionGranted,
	]);

	useEffect(() => {
		if ((isEditing || isConvertingUpcomingToActive) && siteData) {
			const store = useAddNewSiteStore.getState();

			if (!store.siteInfo.siteName && !store.siteInfo.siteLocation) {
				const typeOfContractorDisplay = siteData.type_of_contracts ? siteData.type_of_contracts.replace(/_/g, ' ') : '';
				formik.setValues({
					siteName: siteData?.name,
					siteLocation: siteData.site_location_c,
					siteAddress: siteData.address_c ?? '',
					longitude: siteData.longitude,
					latitude: siteData.latitude,
					pincode: siteData.pincode_c,
					city: siteData.town_name_c,
					siteType: siteData.site_sub_type_c,
					typeOfProperty: siteData.type_of_property,
					propertyArea: siteData.application_area_c,
					siteStage: isConvertingUpcomingToActive ? SiteStageLabel.Active : siteData.site_current_status_c,
					is_bathroom: siteData.is_bathroom ?? false,
					is_other_rooms: siteData.is_other_rooms ?? false,
					bathroom_work_status: siteData.bathroom_work_status ?? '',
					other_rooms_work_status: siteData.other_rooms_work_status ?? '',
					typeOfContractor: typeOfContractorDisplay, // View-only field, NOT saved to store or payload
					contractorPotentialPerMonth: siteData.contractor_potential_per_month ?? '', // View-only field, NOT saved to store or payload
					noOfSites: siteData.no_of_sites ?? '', // View-only field, NOT saved to store or payload
				});
			}
		}

		// eslint-disable-next-line
	}, [siteData, isEditing, isConvertingUpcomingToActive]);

	// Show initial loader while checking permissions
	if (isInitializing && !isEditing) {
		return (
			<div className="add-new-site-form">
				<div className="site-information-initial-loader">
					<div className="loader-content">
						<div className="spinner"></div>
						<h3>Loading Site Information Form...</h3>
						<p>Checking location permissions and initializing form</p>
					</div>
				</div>
			</div>
		);
	}
	return (
		<div className="add-new-site-form">
			<form onSubmit={formik.handleSubmit}>
				<ul className="form-wrapper row">
					<li className="form-control d-col d-col-2">
						<TextInput
							label="Site Name"
							type="text"
							id="siteName"
							name="siteName"
							setFieldValue={formik.setFieldValue}
							value={formik.values.siteName ?? ''}
							error={formik.touched.siteName && formik.errors.siteName ? formik.errors.siteName : null}
							onBlur={formik.handleBlur}
							isAutoFocus
							required
							disabled={isEditing}
						/>
					</li>

					<li className="form-control d-col d-col-2 location">
						<TextInput
							label="Site Location"
							type="text"
							id="siteLocation"
							name="siteLocation"
							setFieldValue={formik.setFieldValue}
							value={formik.values.siteLocation ?? ''}
							error={formik.touched.siteLocation && formik.errors.siteLocation ? formik.errors.siteLocation : null}
							onBlur={formik.handleBlur}
							required
							disabled={isEditing}
						/>
						<button
							type="button"
							aria-label="Google Map Modal"
							className="icon-location map"
							disabled={Boolean(formik.values.latitude || formik.values.longitude) || isEditing}
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							label="Site Address"
							type="text"
							id="siteAddress"
							name="siteAddress"
							setFieldValue={formik.setFieldValue}
							value={formik.values.siteAddress ?? ''}
							onBlur={formik.handleBlur}
							disabled={isEditing}
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							label="Longitude"
							type="text"
							id="longitude"
							name="longitude"
							setFieldValue={formik.setFieldValue}
							value={formik.values.longitude ?? ''}
							onBlur={formik.handleBlur}
							disabled={true}
							required
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							label="Latitude"
							type="text"
							id="latitude"
							name="latitude"
							setFieldValue={formik.setFieldValue}
							value={formik.values.latitude ?? ''}
							onBlur={formik.handleBlur}
							disabled={true}
							required
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							label="Pincode"
							type="text"
							id="pincode"
							name="pincode"
							setFieldValue={formik.setFieldValue}
							value={formik.values.pincode ?? ''}
							onBlur={formik.handleBlur}
							disabled={true}
							required
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							label="City"
							type="text"
							id="city"
							name="city"
							setFieldValue={formik.setFieldValue}
							value={formik.values.city ?? ''}
							onBlur={formik.handleBlur}
							disabled={true}
							required
						/>
					</li>
					<li className="d-col d-col-2">
						<RadioButtonChip
							label="Site Type"
							options={siteTypeList}
							value={siteType ?? ''}
							onChange={(val) => {
								formik.setFieldValue('siteType', val);
								formik.setFieldValue('typeOfProperty', '');
								formik.setFieldValue('propertyArea', '');
							}}
							onBlur={() => formik.setFieldTouched('siteType', true)}
							error={formik.touched.siteType && formik.errors.siteType ? formik.errors.siteType : null}
							required
							disabled={isEditing}
						/>
					</li>
					{siteType === SiteTypeLabel.Residential && (
						<>
							<li className="d-col d-col-2">
								<RadioButtonChip
									label="Type of Property"
									options={residentialTypeOfPropertyList}
									value={formik.values.typeOfProperty ?? ''}
									onChange={(val) => formik.setFieldValue('typeOfProperty', val)}
									onBlur={() => formik.setFieldTouched('typeOfProperty', true)}
									error={
										formik.touched.typeOfProperty && formik.errors.typeOfProperty ? formik.errors.typeOfProperty : null
									}
									required
									disabled={isEditing}
								/>
							</li>
							<li className="d-col d-col-2">
								<Dropdown
									label="Area in Sq Ft"
									id="propertyArea"
									name="propertyArea"
									onBlur={formik.handleBlur}
									title={formik.values.propertyArea ?? ''}
									error={formik.touched.propertyArea && formik.errors.propertyArea ? formik.errors.propertyArea : null}
									options={propertyAreaList}
									setFieldValue={formik.setFieldValue}
									required
									disabled={isEditing}
								/>
							</li>
						</>
					)}
					{siteType === SiteTypeLabel.Commercial && (
						<>
							<li className="form-control d-col d-col-2">
								<RadioButtonChip
									label="Type of Property"
									options={commercialTypeOfPropertyList}
									value={formik.values.typeOfProperty ?? ''}
									onChange={(val) => formik.setFieldValue('typeOfProperty', val)}
									onBlur={() => formik.setFieldTouched('typeOfProperty', true)}
									error={
										formik.touched.typeOfProperty && formik.errors.typeOfProperty ? formik.errors.typeOfProperty : null
									}
									required
									disabled={isEditing}
								/>
							</li>
							<li className="d-col d-col-2">
								<Dropdown
									label="Area in Sq Ft"
									id="propertyArea"
									name="propertyArea"
									onBlur={formik.handleBlur}
									title={formik.values.propertyArea ?? ''}
									error={formik.touched.propertyArea && formik.errors.propertyArea ? formik.errors.propertyArea : null}
									options={propertyAreaList}
									setFieldValue={formik.setFieldValue}
									required
									disabled={isEditing}
								/>
							</li>
						</>
					)}
					<li className="form-control d-col d-col-2">
						<RadioButtonChip
							label="Site Stage"
							options={siteStageList}
							value={formik.values.siteStage ?? ''}
							onChange={(val) => formik.setFieldValue('siteStage', val)}
							onBlur={() => formik.setFieldTouched('siteStage', true)}
							error={formik.touched.siteStage && formik.errors.siteStage ? formik.errors.siteStage : null}
							required
							disabled={isEditing || isConvertingUpcomingToActive}
						/>
					</li>
					{formik.values.siteStage === SiteStageLabel.Upcoming && (
						<li className="d-col d-col-2">
							<SquareCheckboxGroup label="Tiling Work" required>
								{TilingWorkList.map((work) => {
									const isBathroom = work.value === 'Bathroom';
									const isOtherRooms = work.value === 'Other Rooms';
									return (
										<div key={work.value}>
											<SquareCheckbox
												id={work.value}
												name="room"
												value={work.value}
												label={work.label}
												checked={
													(formik.values.is_bathroom && isBathroom) || (formik.values.is_other_rooms && isOtherRooms)
												}
												onChange={() => {
													if (isBathroom) {
														formik.setFieldValue('is_bathroom', !formik.values.is_bathroom);
														if (formik.values.is_bathroom) {
															formik.setFieldValue('bathroom_work_status', '');
														}
													}
													if (isOtherRooms) {
														formik.setFieldValue('is_other_rooms', !formik.values.is_other_rooms);
														if (formik.values.is_other_rooms) {
															formik.setFieldValue('other_rooms_work_status', '');
														}
													}
												}}
												disabled={isEditing}
											/>

											{isBathroom && formik.values.is_bathroom && (
												<RoundedCheckboxGroup label="Tiling Work Status" required>
													{TilingWorkStatusList.map((status) => (
														<RoundedCheckbox
															key={`bathroom-${status.id}`}
															id={`bathroom-${status.id}`}
															name="bathroom-status"
															value={status.value}
															label={status.value}
															checked={(formik.values.bathroom_work_status || '')
																.split(',')
																.map((s: string) => s.trim())
																.includes(status.value)}
															onChange={() => {
																const current = (formik.values.bathroom_work_status || '')
																	.split(',')
																	.map((s: string) => s.trim())
																	.filter(Boolean);
																const exists = current.includes(status.value);
																const next = exists
																	? current.filter((v: string) => v !== status.value)
																	: [...current, status.value];
																formik.setFieldValue('bathroom_work_status', next.join(', '));
															}}
															disabled={isEditing}
														/>
													))}
												</RoundedCheckboxGroup>
											)}

											{isOtherRooms && formik.values.is_other_rooms && (
												<RoundedCheckboxGroup label="Tiling Work Status" required>
													{TilingWorkStatusList.map((status) => (
														<RoundedCheckbox
															key={`other-rooms-${status.id}`}
															id={`other-rooms-${status.value}`}
															name="other-rooms-status"
															value={status.value}
															label={status.value}
															checked={(formik.values.other_rooms_work_status || '')
																.split(',')
																.map((s: string) => s.trim())
																.includes(status.value)}
															onChange={() => {
																const current = (formik.values.other_rooms_work_status || '')
																	.split(',')
																	.map((s: string) => s.trim())
																	.filter(Boolean);
																const exists = current.includes(status.value);
																const next = exists
																	? current.filter((v: string) => v !== status.value)
																	: [...current, status.value];
																formik.setFieldValue('other_rooms_work_status', next.join(', '));
															}}
															disabled={isEditing}
														/>
													))}
												</RoundedCheckboxGroup>
											)}
										</div>
									);
								})}
							</SquareCheckboxGroup>
						</li>
					)}

					{/* Type of Contractor - View only field, NOT included in payload */}
					{/* View-only fields - NOT included in payload (add or edit mode) */}
					{isEditing && (
						<li className="form-control d-col d-col-2">
							<TextInput
								label="Type of Contract"
								type="text"
								id="typeOfContractor"
								name="typeOfContractor"
								setFieldValue={formik.setFieldValue}
								value={formik.values.typeOfContractor ?? ''}
								onBlur={formik.handleBlur}
								disabled={true}
							/>
						</li>
					)}

					{isEditing && (
						<li className="form-control d-col d-col-2">
							<TextInput
								label="Contractor Potential Per Month"
								type="text"
								id="contractorPotentialPerMonth"
								name="contractorPotentialPerMonth"
								setFieldValue={formik.setFieldValue}
								value={formik.values.contractorPotentialPerMonth ?? ''}
								onBlur={formik.handleBlur}
								disabled={true}
							/>
						</li>
					)}

					{isEditing && (
						<li className="form-control d-col d-col-2">
							<TextInput
								label="No of Sites"
								type="text"
								id="noOfSites"
								name="noOfSites"
								setFieldValue={formik.setFieldValue}
								value={formik.values.noOfSites ?? ''}
								onBlur={formik.handleBlur}
								disabled={true}
							/>
						</li>
					)}
				</ul>
				<SubmitButton
					isDisabled={
						isEditing ||
						!formik.isValid ||
						!formik.dirty ||
						(!isEditing && !locationPermissionGranted) ||
						(formik.values.siteStage === SiteStageLabel.Upcoming &&
							((!formik.values.is_bathroom && !formik.values.is_other_rooms) ||
								(formik.values.is_bathroom && !formik.values.bathroom_work_status) ||
								(formik.values.is_other_rooms && !formik.values.other_rooms_work_status)))
					}
					handleSubmitClick={formik.handleSubmit}
				/>
			</form>
			{/* Location Permission Denied Message */}
			{locationPermissionDenied && !isEditing && (
				<div className="location-permission-denied">
					<div className="permission-denied-content">
						<h3>Location Permission Required</h3>
						<p>
							This site creation requires location access. Please enable location permission in your browser settings.
						</p>
						<button className="retry-location-btn" onClick={requestLocationPermission}>
							Try Again
						</button>
					</div>
				</div>
			)}

			{/* Location Permission Modal - Common for all cases */}
			{!isEditing && (
				<LocationModal
					isOpen={showLocationPermissionModal}
					onClose={handleModalClose}
					onLocationSuccess={handleLocationSuccess}
					onLocationError={handleLocationError}
					title="Location Access Required"
					message="Location access is required to create a new site. Please allow location access when prompted by your browser."
					allowClose={false} // Disable close button for mandatory location
				/>
			)}
		</div>
	);
};
